import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import type { Grade } from '../../../types';
import type { ComparisonStudentRow, ExamComparison } from '../../../services/studentReportsService';
import {
  Page, DataTable, fitHeaderStyle, pdfColors, pdfSpacing, fmtClassName,
  type DataTableColumn, type SchoolBranding,
} from '../../../utils/pdf';
import { MARK_GREEN, MARK_RED, PctPill, gradeColorOf } from './scoreMarks';

interface Props {
  data: ExamComparison;
  grades: Grade[];
  branding: SchoolBranding;
  generatedAt: string;
}

// A4 landscape inside the page frame.
const CONTENT_W = 762;
const STUDENT_MIN_W = 120;

const styles = StyleSheet.create({
  strong: { fontWeight: 600, color: pdfColors.textPrimary },
  faint: { color: pdfColors.textFaint },
  up: { fontWeight: 700, color: MARK_GREEN },
  down: { fontWeight: 700, color: MARK_RED },
  flat: { fontWeight: 600, color: pdfColors.textMuted },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted, fontStyle: 'italic',
    borderWidth: 0.6, borderColor: pdfColors.dividerStrong, borderStyle: 'dashed', borderRadius: 4,
  },
});

/** The change from the first exam to the last, in points — up green, down red. */
const Change: React.FC<{ s: ComparisonStudentRow }> = ({ s }) => {
  if (s.trend === 'na' || s.deltaPct == null) return <Text style={styles.faint}>—</Text>;
  if (s.trend === 'up') return <Text style={styles.up}>+{s.deltaPct.toFixed(1)}</Text>;
  if (s.trend === 'down') return <Text style={styles.down}>−{Math.abs(s.deltaPct).toFixed(1)}</Text>;
  return <Text style={styles.flat}>0.0</Text>;
};

/**
 * Exams compared for a class or section: the school's letterhead, then every
 * student with their percentage in each exam (in its grade's colour), their
 * average across them and how far they moved from the first to the last.
 */
const ExamComparisonDocument: React.FC<Props> = ({ data, grades, branding, generatedAt }) => {
  const gradeColor = gradeColorOf(grades);
  const cls = fmtClassName(data.class.name);
  const scope = data.section ? `${cls} · Section ${data.section.name}` : cls;
  const fixed = 30 + 32 + (data.section ? 0 : 46) + 54 + 52;
  const examW = Math.max(52, Math.min(96, (CONTENT_W - fixed - STUDENT_MIN_W) / Math.max(data.exams.length, 1)));
  const examHeader = fitHeaderStyle(data.exams.map(e => e.name), examW);

  const columns: Array<DataTableColumn<ComparisonStudentRow>> = [
    { key: 'n', header: '#', width: 30, align: 'center', render: (_s, i) => <Text style={styles.strong}>{i + 1}</Text> },
    { key: 'roll', header: 'Roll', width: 32, align: 'center', render: s => String(s.rollNumber) },
    { key: 'name', header: 'Student', flex: 1, render: s => <Text style={styles.strong}>{s.name}</Text> },
    ...(data.section ? [] : [{ key: 'section', header: 'Section', width: 46, align: 'center' as const, render: (s: ComparisonStudentRow) => s.sectionName || '—' }]),
    ...data.exams.map((e, k): DataTableColumn<ComparisonStudentRow> => ({
      key: `e${k}`, header: e.name, width: examW, align: 'center', headerStyle: examHeader,
      render: s => <PctPill pct={s.perExam[k]?.overallPct ?? null} grades={grades} />,
    })),
    { key: 'avg', header: 'Average', width: 54, align: 'center', render: s => <Text style={{ fontWeight: 700, color: gradeColor(s.averagePct) }}>{s.averagePct.toFixed(1)}%</Text> },
    { key: 'change', header: 'Change', width: 52, align: 'center', render: s => <Change s={s} /> },
  ];

  return (
    <Document title={`Exam Comparison — ${scope}`} author={branding.name}>
      <Page
        branding={branding}
        orientation="landscape"
        meta={{ title: `Exam Comparison — ${scope}`, subtitle: `${data.session.name}  ·  ${data.exams.length} exams`, generatedAt }}
        official={{ details: [
          { label: 'Session', value: data.session.name },
          { label: 'Class', value: cls },
          ...(data.section ? [{ label: 'Section', value: data.section.name }] : []),
          { label: 'Exams', value: `${data.exams.length} compared` },
        ], perRow: 4 }}
      >
        {data.students.length === 0
          ? <Text style={styles.empty}>No students are enrolled in this class for these exams.</Text>
          : <DataTable columns={columns} rows={data.students} compact small />}
      </Page>
    </Document>
  );
};

export default ExamComparisonDocument;
